import {useEffect, useRef, useState, type ReactNode, type RefObject} from 'react';
import {cn} from '@chekinapp/ui';
import {getMaxOvalSize} from './faceGuideGeometry';
import styles from './faceGuide.module.css';

type FaceGuideState = 'searching' | 'aligning' | 'centered' | 'scanning';

const HOLD_MS = 1200;
const RING_FILL_MS = Math.round(HOLD_MS * 0.8);
const STROKE: Record<FaceGuideState, string> = {
  searching: 'rgba(255,255,255,0.6)',
  aligning: 'var(--chekin-amber-500)',
  centered: 'var(--chekin-color-brand-blue)',
  scanning: 'rgba(255,255,255,0.6)',
};

type FaceGuideSize = 'default' | 'max';

const SILHOUETTE_LINGER_MS = 1500;

type FaceGuideProps = {
  state: FaceGuideState;
  progress?: number;
  dim?: boolean;
  size?: FaceGuideSize;
  silhouette?: ReactNode;
  live?: boolean;
  className?: string;
};

function useMaxOvalSize(ref: RefObject<HTMLDivElement | null>, enabled: boolean) {
  const [size, setSize] = useState<{width: number; height: number} | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!enabled || !element) return;

    const measure = () => {
      const {width, height} = element.getBoundingClientRect();
      if (!width || !height) return;
      setSize(getMaxOvalSize(width, height));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, enabled]);

  return size;
}

function useRingLength(ref: RefObject<SVGEllipseElement | null>, active: boolean) {
  const [length, setLength] = useState(0);

  useEffect(() => {
    const ellipse = ref.current;
    const svg = ellipse?.ownerSVGElement;
    if (!active || !ellipse || !svg) return;

    const measure = () => {
      const ctm = ellipse.getScreenCTM();
      const scale = ctm ? Math.sqrt(Math.abs(ctm.a * ctm.d - ctm.b * ctm.c)) : 1;
      setLength(ellipse.getTotalLength() * scale);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(svg);
    return () => observer.disconnect();
  }, [ref, active]);

  return length;
}

const RING_OVERLAP = 4;
const HIDDEN_DASH = '0px 9999px';

function FaceGuide({
  state,
  progress,
  dim = true,
  size = 'default',
  silhouette,
  live = false,
  className,
}: FaceGuideProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const [hasSilhouetteFaded, setHasSilhouetteFaded] = useState(false);
  const isMax = size === 'max';
  const maxOvalSize = useMaxOvalSize(stageRef, isMax);

  useEffect(() => {
    if (!live) {
      setHasSilhouetteFaded(false);
      return;
    }
    const timeout = setTimeout(() => setHasSilhouetteFaded(true), SILHOUETTE_LINGER_MS);
    return () => clearTimeout(timeout);
  }, [live]);
  const holdRingRef = useRef<SVGEllipseElement>(null);
  const progressRingRef = useRef<SVGEllipseElement>(null);
  const showHoldRing = state === 'centered' && progress == null;
  const showProgressRing = progress != null;
  const clampedProgress = Math.max(0, Math.min(100, progress ?? 0));
  const holdRingLength = useRingLength(holdRingRef, showHoldRing);
  const progressRingLength = useRingLength(progressRingRef, showProgressRing);

  const [ringArmed, setRingArmed] = useState(false);
  useEffect(() => {
    if (state !== 'centered') {
      setRingArmed(false);
      return;
    }
    const id = requestAnimationFrame(() => setRingArmed(true));
    return () => cancelAnimationFrame(id);
  }, [state]);

  const attention = state === 'aligning';
  const glowColor =
    state === 'centered'
      ? 'var(--chekin-color-brand-blue)'
      : state === 'aligning'
      ? 'var(--chekin-amber-500)'
      : 'transparent';

  return (
    <div
      ref={stageRef}
      aria-hidden
      data-slot="face-guide"
      data-size={size}
      className="pointer-events-none absolute inset-0 z-[2]"
    >
      <div
        className={cn(
          'absolute left-1/2',
          isMax
            ? '-translate-x-1/2 -translate-y-1/2 top-1/2'
            : 'top-[8%] aspect-[3/4] h-[74%] -translate-x-1/2',
          isMax && !maxOvalSize && 'aspect-[0.78] h-[90%]',
          attention && 'animate-pulse',
          className,
        )}
        style={maxOvalSize ?? undefined}
      >
        {dim && (
          <div
            className="absolute inset-0 rounded-[50%]"
            style={{boxShadow: '0 0 0 9999px var(--capture-scrim)'}}
          />
        )}
        {state === 'searching' && (
          <div
            data-slot="face-guide-pulse"
            className={cn('absolute inset-0 rounded-[50%]', styles.pulse)}
          />
        )}
        {silhouette && (
          <div className="absolute inset-0 overflow-hidden rounded-[50%]">
            <div
              data-slot="face-guide-silhouette"
              className={cn(
                'absolute left-[-30%] top-[-2%] w-[160%] [mask-image:linear-gradient(to_bottom,#000_70%,transparent_94%)] [transition:opacity_600ms_ease_600ms] [&>img]:block [&>img]:h-auto [&>img]:w-full',
                hasSilhouetteFaded
                  ? 'opacity-0'
                  : live
                  ? 'opacity-[0.26]'
                  : 'opacity-[0.7]',
              )}
            >
              {silhouette}
            </div>
          </div>
        )}
        <svg
          className="absolute inset-0 h-full w-full overflow-visible"
          viewBox="0 0 200 264"
          preserveAspectRatio="none"
          fill="none"
        >
          <ellipse
            cx="100"
            cy="132"
            rx="98"
            ry="130"
            vectorEffect="non-scaling-stroke"
            strokeWidth={3}
            strokeLinecap="round"
            style={{
              stroke: STROKE[state],
              transition: 'stroke 250ms ease',
              filter: `drop-shadow(0 0 5px ${glowColor})`,
            }}
          />

          {showHoldRing && (
            <ellipse
              ref={holdRingRef}
              cx="100"
              cy="132"
              rx="98"
              ry="130"
              vectorEffect="non-scaling-stroke"
              strokeWidth={4}
              strokeLinecap="round"
              style={{
                stroke: 'var(--chekin-green-700)',
                strokeDasharray: holdRingLength
                  ? `${
                      ringArmed ? holdRingLength + RING_OVERLAP : 0
                    }px ${holdRingLength}px`
                  : HIDDEN_DASH,
                transition: `stroke-dasharray ${RING_FILL_MS}ms linear`,
                filter: 'drop-shadow(0 0 4px var(--chekin-green-700))',
              }}
            />
          )}

          {showProgressRing && (
            <ellipse
              ref={progressRingRef}
              cx="100"
              cy="132"
              rx="130"
              ry="98"
              vectorEffect="non-scaling-stroke"
              strokeWidth={4}
              strokeLinecap="round"
              transform="rotate(-90 100 132)"
              style={{
                stroke: 'var(--chekin-green-700)',
                strokeDasharray: progressRingLength
                  ? `${progressRingLength + RING_OVERLAP}px ${progressRingLength}px`
                  : HIDDEN_DASH,
                strokeDashoffset: `${
                  (progressRingLength + RING_OVERLAP) * (1 - clampedProgress / 100)
                }px`,
                transition: 'stroke-dashoffset 200ms ease-out',
                filter: 'drop-shadow(0 0 4px var(--chekin-green-700))',
              }}
            />
          )}
        </svg>
      </div>
    </div>
  );
}

export {FaceGuide};
export type {FaceGuideProps, FaceGuideSize, FaceGuideState};
